import type { CurrentUser } from '@capsio/contracts';
import { useEffect, useState, type ReactNode } from 'react';
import { ArchivePage } from './ArchivePage.js';
import { MembersPage } from './MembersPage.js';
import { OrganizationPage } from './OrganizationPage.js';

/**
 * 设置里的分区。
 *
 * 「组织架构」已撤（C-181 批三）：组织架构的录入在「ATC 设置」页的资产台账——
 * 那是业务侧录入的唯一入口。两个入口就是两套真相。
 *
 * 「发信邮箱」那一页（`MailSettingsPage`）还在代码里，等消息通道定了加回来。
 */
export type SettingsSection = 'organization' | 'members' | 'archive';

const SECTIONS: { key: SettingsSection; label: string }[] = [
  { key: 'organization', label: '企业' },
  { key: 'members', label: '成员' },
  { key: 'archive', label: '归档' },
];

/**
 * 设置弹窗，左右结构：左边分区，右边内容。
 *
 * 做成盖在系统之上的一层而不是换个视图：设置是「这套系统怎么配」，
 * 关掉之后资产树的选中状态和滚动位置都还在原处。
 */
export function SettingsDialog({
  me,
  organizationName,
  onOrganizationChanged,
  onClose,
}: {
  me: CurrentUser;
  /** 树根显示的名字。企业本身不是一条节点记录，名字得从外面传进来。 */
  organizationName: string;
  /** 企业信息改过了：名称和标识在侧栏、面包屑、账号浮层上都显示着，得重新拉一次。 */
  onOrganizationChanged: () => void;
  onClose: () => void;
}): ReactNode {
  const [section, setSection] = useState<SettingsSection>('organization');

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="backdrop dialog-layer"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="settings-title">
        <header className="dialog-head">
          <h2 id="settings-title">设置</h2>
          <button className="iconbtn" type="button" onClick={onClose} aria-label="关闭设置">
            <svg width="13" height="13" viewBox="0 0 13 13" aria-hidden="true">
              <g fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                <path d="M2.5 2.5 L10.5 10.5" />
                <path d="M10.5 2.5 L2.5 10.5" />
              </g>
            </svg>
          </button>
        </header>

        <div className="dialog-body">
          <nav className="setnav">
            {SECTIONS.map((item) => (
              <button
                key={item.key}
                type="button"
                aria-current={section === item.key}
                onClick={() => setSection(item.key)}
              >
                <span>{item.label}</span>
              </button>
            ))}
          </nav>

          <div className="setting-content">
            {section === 'organization' && <OrganizationPage onSaved={onOrganizationChanged} />}
            {section === 'members' && <MembersPage me={me} />}
            {section === 'archive' && <ArchivePage rootName={organizationName} />}
          </div>
        </div>
      </div>
    </div>
  );
}
